/* block-buy-buttons.js
   --------------------------------------------------
   Updated 14 May 2025
   – Ensures button label persists after theme re‑renders
--------------------------------------------------- */

import { EVENTS, publish, subscribe } from '@archetype-themes/utils/pubsub';

class BlockBuyButtons extends HTMLElement {
  /* ----------------- Lifecycle ------------------ */
  connectedCallback() {
    this.addEventListener('submit', this.handleSubmit.bind(this));

    this.variantChangeUnsubscriber = subscribe(
      `${EVENTS.variantChange}:${this.dataset.sectionId}:${this.dataset.productId}`,
      this.handleVariantChange.bind(this)
    );

    this.cartType = this.dataset.cartType;
  }
  disconnectedCallback() {
    this.variantChangeUnsubscriber();
  }

  /* ----------------- Variant handling ------------------ */
  handleVariantChange({ detail }) {
    const { html, variant } = detail;

    if (!variant) {
      this.toggleAddButton(true, this.getLocales().unavailable);
      return;
    }

    this.updateVariantInput(variant);
    this.renderProductInfo(html);
  }

  renderProductInfo(html) {
    /* Button HTML that Archetype just rendered for this variant */
    const addButtonUpdated = html.getElementById(
      `ProductSubmitButton-${this.dataset.sectionId}`
    );
    if (!addButtonUpdated) return;

    /* Grab whatever label the theme meant to show */
    const label =
      addButtonUpdated.querySelector('.btn__text')?.textContent.trim() ||
      (addButtonUpdated.hasAttribute('disabled')
        ? this.getLocales().soldOut
        : this.getLocales().addToCart);

    this.toggleAddButton(
      addButtonUpdated.hasAttribute('disabled'),
      label
    );
  }

  /* ----------------- Locales (safe fallback) ------------------ */
  getLocales() {
    if (!this.locales) {
      const el = this.querySelector('[type="application/json"]');
      this.locales = el
        ? JSON.parse(el.textContent)
        : {
            addToCart: 'Add to cart',
            preOrder: 'Pre‑order',
            soldOut: 'Sold out',
            unavailable: 'Unavailable'
          };
    }
    return this.locales;
  }

  /* ----------------- Button toggle ------------------ */
  toggleAddButton(disable = true, label) {
    const productForm = this.querySelector(
      `#product-form-${this.dataset.sectionId}`
    );
    if (!productForm) return;

    const addButton = productForm.querySelector('[name="add"]');
    if (!addButton) return;

    /* Ensure <span class="btn__text"> exists */
    let addButtonText = addButton.querySelector('span.btn__text');
    if (!addButtonText) {
      addButtonText = document.createElement('span');
      addButtonText.classList.add('btn__text');
      addButton.appendChild(addButtonText);
    }

    /* Pick label: explicit > template‑specific > default */
    const finalLabel =
      label ||
      (this.dataset.template === 'preorder'
        ? this.getLocales().preOrder
        : this.getLocales().addToCart);

    addButtonText.textContent = finalLabel;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
    } else {
      addButton.removeAttribute('disabled');
    }
  }

  /* ----------------- Variant input updates ------------------ */
  updateVariantInput(variant) {
    const productForms = this.querySelectorAll(
      `#product-form-${this.dataset.sectionId}, #product-form-installment-${this.dataset.sectionId}`
    );
    productForms.forEach((form) => {
      form.querySelector('input[name="id"]').value = variant.id;
      form
        .querySelector('input[name="id"]')
        .dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ----------------- Submit handler ------------------ */
  async handleSubmit(event) {
    if (this.cartType === 'page') return;
    event.preventDefault();
    this.disableAddToCartButton();

    try {
      const responseJson = await this.addVariantToCart();
      publish(EVENTS.ajaxProductAdded, {
        detail: {
          product: responseJson,
          addToCartBtn: this.querySelector(
            `#ProductSubmitButton-${this.dataset.sectionId}`
          )
        }
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      this.enableAddToCartButton();
    }
  }

  /* ----------------- Error handling ------------------ */
  handleError(error) {
    if (!error.description) {
      console.warn(error);
      return;
    }
    const form = this.querySelector('form');
    form.querySelector('.errors')?.remove();
    const div = document.createElement('div');
    div.classList.add('errors', 'text-center');
    div.textContent =
      typeof error.description === 'object' ? error.message : error.description;
    form.append(div);

    publish(EVENTS.ajaxProductError, {
      detail: { errorMessage: error.description }
    });
  }

  /* ----------------- AJAX helpers ------------------ */
  async addVariantToCart() {
    const data = this.getFormDataWithSections();
    const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: data
    });
    if (!res.ok) throw await res.json();
    return res.json();
  }
  async fetchCart() {
    return (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
  }
  getFormDataWithSections() {
    const form = this.querySelector(
      `#product-form-${this.dataset.sectionId}`
    );
    const fd = new FormData(form);
    fd.set(
      'sections_url',
      `${window.Shopify.routes.root}variants/${form.id.value}`
    );
    return fd;
  }

  /* ----------------- Button state helpers ------------------ */
  enableAddToCartButton() {
    const form = this.querySelector(
      `#product-form-${this.dataset.sectionId}`
    );
    if (!form) return;
    const btn = form.querySelector('[name="add"]');
    btn.removeAttribute('aria-busy');
    btn.classList.remove('btn--loading');
  }
  disableAddToCartButton() {
    const form = this.querySelector(
      `#product-form-${this.dataset.sectionId}`
    );
    form.querySelector('.errors')?.remove();
    if (!form) return;
    const btn = form.querySelector('[name="add"]');
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('btn--loading');
  }
}

/* Register the custom element */
customElements.define('block-buy-buttons', BlockBuyButtons);
